// React
import React, { useState, useEffect, useMemo } from 'react';

// Assets
import { CloseCircleFilled, SearchOutlined } from '@ant-design/icons';

// Packages
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    OutlinedInput,
    Stack,
    Typography
} from '@mui/material';

// Components
import LoadingButton from 'components/@extended/LoadingButton';

const normalize = value => (value || '').toString().toLowerCase();

const Intercom = ({
    orgs,
    panels,
    userData,
    creatingPanelIntercom,
    handleClose,
    setActiveManagerOrg,
    changingFocus
}) => {
    const [preppingIntercomState, setPreppingIntercomState] = useState(false);
    const [com, setCom] = useState(null);
    const [search, setSearch] = useState('');

    // Orgs with at least one kiosk, narrowed by the search. Every search term must appear in
    // "<org name> <panel name>", so "boca main" finds "Boca Bay Main Gate".
    const groups = useMemo(() => {
        const terms = normalize(search).split(/\s+/).filter(Boolean);

        return (orgs || [])
            .filter(org => (panels?.[org.org_id] || []).length > 0)
            .map(org => ({
                orgId: org.org_id,
                orgName: org.org_name,
                panels: panels[org.org_id].filter(panel => {
                    const haystack = normalize(`${org.org_name} ${panel.name}`);
                    return terms.every(term => haystack.includes(term));
                })
            }))
            .filter(group => group.panels.length > 0);
    }, [orgs, panels, search]);

    const handleCreatingIntercom = com => {
        if (preppingIntercomState) return;

        if (userData.active_org_id !== com.org_id) {
            const org = orgs.find(org => org.org_id === com.org_id);
            setActiveManagerOrg({ ...org, origin: 'intercom' });
        }
        setCom(com);
        setPreppingIntercomState(true);
    };

    const startPanel = (orgId, panel) =>
        handleCreatingIntercom({
            connected_by: userData.uid,
            connection_end: null,
            connection_start: null,
            org_id: orgId,
            panel_id: panel.id,
            panel_name: panel.name,
            status: 'pending'
        });

    // Enter calls the gate when the search narrows down to exactly one.
    const handleSearchKeyDown = e => {
        if (e.key !== 'Enter') return;
        if (groups.length === 1 && groups[0].panels.length === 1) {
            startPanel(groups[0].orgId, groups[0].panels[0]);
        }
    };

    useEffect(() => {
        if (preppingIntercomState) {
            if (com && com.org_id === userData.active_org_id && !changingFocus) {
                creatingPanelIntercom({ com });
                handleClose();
                setPreppingIntercomState(false);
                setCom(null);
            }
        }
    }, [
        preppingIntercomState,
        userData,
        com,
        changingFocus,
        creatingPanelIntercom,
        setPreppingIntercomState,
        setCom,
        handleClose
    ]);

    return (
        <Stack sx={{ flex: 1, minHeight: 0 }}>
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                <OutlinedInput
                    autoFocus
                    fullWidth
                    placeholder="Search by community or gate"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    inputProps={{ 'aria-label': 'Search intercoms' }}
                    startAdornment={
                        <InputAdornment position="start">
                            <SearchOutlined />
                        </InputAdornment>
                    }
                    endAdornment={
                        search && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    edge="end"
                                    aria-label="Clear search"
                                    onClick={() => setSearch('')}
                                >
                                    <CloseCircleFilled />
                                </IconButton>
                            </InputAdornment>
                        )
                    }
                />
            </Box>

            <List key="panels-list" sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 0 }}>
                {groups.length === 0 && (
                    <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                        {search
                            ? `No gates match "${search.trim()}"`
                            : 'No intercoms available'}
                    </Typography>
                )}

                {groups.map(group => (
                    <Stack key={group.orgId}>
                        <Typography
                            variant="h5"
                            color="secondary"
                            marginY={2}
                            fontWeight={700}
                        >
                            {group.orgName}
                        </Typography>
                        {group.panels.map(panel => {
                            const isStarting =
                                preppingIntercomState && com?.panel_id === panel.id;

                            return (
                                <LoadingButton
                                    key={panel.id}
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    sx={{ marginBottom: 2 }}
                                    size="large"
                                    loading={isStarting}
                                    disabled={preppingIntercomState && !isStarting}
                                    onClick={() => startPanel(group.orgId, panel)}
                                >
                                    {panel.name}
                                </LoadingButton>
                            );
                        })}
                    </Stack>
                ))}
            </List>
        </Stack>
    );
};

export default Intercom;
